// src/pages/Schedule/DeliverySchedule.jsx
import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import {
    FiCalendar, FiChevronLeft, FiChevronRight,
    FiGrid, FiList, FiX, FiMove, FiHash, FiUser,
    FiPackage, FiTruck, FiUsers, FiClock, FiLayers, FiHome
} from "react-icons/fi";

import {
    DndContext,
    DragOverlay,
    useSensor,
    useSensors,
    PointerSensor,
    pointerWithin,
    closestCenter,
    MeasuringStrategy,
    useDndMonitor
} from '@dnd-kit/core';
import { useDraggable, useDroppable, defaultDropAnimationSideEffects } from '@dnd-kit/core';
import { snapCenterToCursor } from '@dnd-kit/modifiers';

import ConfirmationPopup from "../common/ConfirmationPopup";
import useDeviceDetection from "../common/useDeviceDetection";

import { supabase } from "../../config/supabase";

import {
    getScheduledDeliveries,
    getUnscheduledDeliveries,
    updateDeliveryDate
} from "../../lib/supabase/schedule";

import {
    getCompanyDrivers,
    assignDriverToDelivery,
    removeDriverFromDelivery,
    getDriverWorkload
} from "../../lib/supabase/schedule-driver";

const DeliverySchedule = () => {
    const { userProfile, company } = useAuth();
    const navigate = useNavigate();
    const { companySlug } = useParams();
    const { isMobile } = useDeviceDetection();

    const [selectedWeek, setSelectedWeek] = useState("week1");
    const [scheduleData, setScheduleData] = useState({
        week1: {}, week2: {}, week3: {}, week4: {}
    });
    const [unscheduledAssignments, setUnscheduledAssignments] = useState([]);

    const [activeId, setActiveId] = useState(null);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [pendingMove, setPendingMove] = useState(null);

    // Mobile-specific states
    const [showMoveModal, setShowMoveModal] = useState(false);
    const [selectedAssignment, setSelectedAssignment] = useState(null);
    const [mobileViewMode, setMobileViewMode] = useState('grid');
    const [selectedDay, setSelectedDay] = useState(null);

    // Week switching states for drag and drop
    const [dragOverWeek, setDragOverWeek] = useState(null);
    const [switchProgress, setSwitchProgress] = useState(0);
    const [draggedItemCache, setDraggedItemCache] = useState(null);

    const [showOnlyMine, setShowOnlyMine] = useState(false);
    const [selectedUser, setSelectedUser] = useState('');
    const [availableDrivers, setAvailableDrivers] = useState([]);

    const [isLoading, setIsLoading] = useState(false);

    // Driver panel states
    const [drivers, setDrivers] = useState([]);
    const [isPanelOpen, setIsPanelOpen] = useState(false);
    const [currentDropTarget, setCurrentDropTarget] = useState(null);
    const [dragHandleType, setDragHandleType] = useState(null); // 'date' or 'driver'
    const [dropTargetRect, setDropTargetRect] = useState(null);
    const [driverWorkloads, setDriverWorkloads] = useState({});
    const panelRef = useRef(null);

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        })
    );

    const dropAnimationConfig = {
        duration: 200,
        easing: 'cubic-bezier(0.18, 0.67, 0.6, 1.22)',
        sideEffects: defaultDropAnimationSideEffects({
            styles: {
                active: {
                    opacity: '0.5',
                },
            },
        }),
    };

    // Custom collision detection that uses closestCenter when driver panel is open
    const customCollisionDetection = (args) => {
        // When driver panel is open, use closestCenter for better detection
        if (isPanelOpen) {
            return closestCenter(args);
        }
        // Otherwise use pointerWithin for normal date scheduling
        return pointerWithin(args);
    };

    useEffect(() => {
        return () => {
            setActiveId(null);
            setDragOverWeek(null);
            setSwitchProgress(0);
        };
    }, []);

    useEffect(() => {
        if (company?.id) {
            fetchAssignments();
        }
    }, [company, showOnlyMine, selectedUser]);

    // Fetch available drivers for filtering
    useEffect(() => {
        if (userProfile?.role === 'admin' || userProfile?.role === 'super_admin') {
            fetchAvailableDrivers();
        }
    }, [userProfile]);

    const fetchAvailableDrivers = async () => {
        try {
            // Fetch drivers from your users table
            // This is a placeholder - implement based on your actual query
            setAvailableDrivers([]);
        } catch (error) {
            console.error('Error fetching drivers:', error);
        }
    };

    // Fetch drivers for driver assignment panel
    useEffect(() => {
        if (company?.id) {
            fetchDrivers();
        }
    }, [company]);

    const fetchDrivers = async () => {
        try {
            const driversData = await getCompanyDrivers(company.id);
            setDrivers(driversData || []);

            // Fetch workload for each driver
            const workloads = {};
            await Promise.all(
                driversData.map(async (driver) => {
                    const workload = await getDriverWorkload(driver.id, company.id);
                    workloads[driver.id] = workload;
                })
            );
            setDriverWorkloads(workloads);
        } catch (error) {
            console.error('Error fetching drivers:', error);
        }
    };

    const fetchAssignments = async () => {
        try {
            setIsLoading(true);

            const [scheduledRes, unscheduledRes] = await Promise.all([
                getScheduledDeliveries(company.id, {
                    showOnlyMine: showOnlyMine ? userProfile.id : null,
                    userId: selectedUser || null
                }),
                getUnscheduledDeliveries(company.id, {
                    showOnlyMine: showOnlyMine ? userProfile.id : null,
                    userId: selectedUser || null
                })
            ]);

            if (scheduledRes.error || unscheduledRes.error) {
                throw scheduledRes.error || unscheduledRes.error;
            }

            setScheduleData(scheduledRes.data || {
                week1: {}, week2: {}, week3: {}, week4: {}
            });
            setUnscheduledAssignments(unscheduledRes.data || []);
        } catch (error) {
            console.error('Error fetching deliveries:', error);
            setScheduleData({
                week1: {}, week2: {}, week3: {}, week4: {}
            });
            setUnscheduledAssignments([]);
        } finally {
            setIsLoading(false);
        }
    };

    // Helper functions
    const getAustralianDate = () => {
        return new Date(new Date().toLocaleString("en-US", { timeZone: "Australia/Melbourne" }));
    };

    const getWeekLabels = () => {
        const labels = [];
        const today = getAustralianDate();
        const monday = new Date(today);
        monday.setDate(today.getDate() - today.getDay() + 1);

        for (let i = 0; i < 4; i++) {
            const start = new Date(monday);
            start.setDate(monday.getDate() + i * 7);
            const end = new Date(start);
            end.setDate(start.getDate() + 6);

            const label = `${start.toLocaleDateString('en-AU', { day: '2-digit', month: 'short' })} – ${end.toLocaleDateString('en-AU', { day: '2-digit', month: 'short' })}`;
            labels.push(label);
        }

        return labels;
    };

    const getDayDate = (dayIndex) => {
        const today = getAustralianDate();
        const monday = new Date(today);
        monday.setDate(today.getDate() - today.getDay() + 1);

        const weekNumber = parseInt(selectedWeek.replace('week', '')) - 1;
        const targetDate = new Date(monday);
        targetDate.setDate(monday.getDate() + (weekNumber * 7) + dayIndex);

        return targetDate.toLocaleDateString('en-AU');
    };

    const getDayDateObject = (dayIndex) => {
        const today = getAustralianDate();
        const monday = new Date(today);
        monday.setDate(today.getDate() - today.getDay() + 1);

        const weekNumber = parseInt(selectedWeek.replace('week', '')) - 1;
        const targetDate = new Date(monday);
        targetDate.setDate(monday.getDate() + (weekNumber * 7) + dayIndex);

        const utcTargetDate = new Date(Date.UTC(
            targetDate.getFullYear(),
            targetDate.getMonth(),
            targetDate.getDate(),
            12, 0, 0, 0
        ));

        return utcTargetDate;
    };

    const days = [
        { label: "Mon", key: "Monday" },
        { label: "Tue", key: "Tuesday" },
        { label: "Wed", key: "Wednesday" },
        { label: "Thu", key: "Thursday" },
        { label: "Fri", key: "Friday" },
        { label: "Sat", key: "Saturday" },
    ];

    const getMainStatusColor = (status) => {
        if (status === "ready_for_delivery") return 'bg-teal-500';
        if (status === "completed") return 'bg-gray-500';
        return 'bg-gray-400';
    };

    const getQuoteTypeStyle = (quoteType) => {
        switch (quoteType) {
            case 'roof':
                return { icon: FiHome, bgColor: 'bg-blue-100', textColor: 'text-blue-700', label: 'Roof' };
            case 'wall':
                return { icon: FiLayers, bgColor: 'bg-red-100', textColor: 'text-red-700', label: 'Wall' };
            case 'floor':
                return { icon: FiLayers, bgColor: 'bg-green-100', textColor: 'text-green-700', label: 'Floor' };
            case 'timber':
                return { icon: FiPackage, bgColor: 'bg-amber-100', textColor: 'text-amber-700', label: 'Timber' };
            default:
                return { icon: FiPackage, bgColor: 'bg-gray-100', textColor: 'text-gray-700', label: quoteType };
        }
    };

    const todayLabel = getAustralianDate().toLocaleDateString("en-AU", {
        weekday: "short",
        timeZone: "Australia/Melbourne",
    });

    // Drag and Drop Handlers
    const handleDragStart = (event) => {
        const handleType = event.active.data.current?.handleType;

        if (handleType === 'driver') {
            handleDriverDragStart(event);
        } else {
            // Default date drag
            setActiveId(event.active.id);
            setDragHandleType('date');

            const assignment = event.active.data.current?.assignment;
            if (assignment) {
                setDraggedItemCache(assignment);
            }
        }
    };

    const handleDragOver = (event) => {
        if (dragHandleType === 'driver') {
            handleDriverDragOver(event);
        }
    };

    const handleDragEnd = (event) => {
        if (dragHandleType === 'driver' || event.active.data.current?.handleType === 'driver') {
            handleDriverDragEnd(event);
        } else {
            // Original date drag logic
            const { active, over } = event;

            setDragOverWeek(null);
            setSwitchProgress(0);
            const assignmentData = draggedItemCache || active.data.current?.assignment;

            setDraggedItemCache(null);

            if (!over) {
                setActiveId(null);
                return;
            }

            if (over.id.toString().startsWith('week-selector-')) {
                setActiveId(null);
                return;
            }

            if (over.id === 'unscheduled-zone') {
                setPendingMove({
                    assignment: assignmentData,
                    removeDate: true
                });
                setShowConfirmation(true);
            } else if (over.id.startsWith('day-')) {
                const [, weekStr, dayKey] = over.id.split('-');
                const dayIndex = days.findIndex(d => d.key === dayKey);

                if (dayIndex !== -1) {
                    const targetDate = getDayDateObject(dayIndex);
                    const newDate = new Date(targetDate);
                    newDate.setUTCHours(10, 0, 0, 0);

                    setPendingMove({
                        assignment: assignmentData,
                        newDate: newDate,
                        dayKey: dayKey,
                        targetWeek: weekStr
                    });

                    setShowConfirmation(true);
                }
            }

            setActiveId(null);
        }
    };

    const WeekSwitcher = () => {
        const timerRef = useRef(null);
        const progressRef = useRef(null);

        useDndMonitor({
            onDragMove: (event) => {
                const { over } = event;

                if (over?.id && over.id.toString().startsWith('week-selector-')) {
                    const weekIndex = parseInt(over.id.replace('week-selector-', ''));
                    const targetWeek = `week${weekIndex + 1}`;

                    if (targetWeek !== selectedWeek && dragOverWeek !== weekIndex) {
                        setDragOverWeek(weekIndex);
                        setSwitchProgress(0);

                        if (timerRef.current) clearTimeout(timerRef.current);
                        if (progressRef.current) clearInterval(progressRef.current);

                        progressRef.current = setInterval(() => {
                            setSwitchProgress(prev => {
                                if (prev >= 100) {
                                    clearInterval(progressRef.current);
                                    return 100;
                                }
                                return prev + 5;
                            });
                        }, 50);

                        timerRef.current = setTimeout(() => {
                            setSelectedWeek(targetWeek);
                            setDragOverWeek(null);
                            setSwitchProgress(0);
                            if (progressRef.current) clearInterval(progressRef.current);
                            timerRef.current = null;
                            progressRef.current = null;
                        }, 2000);
                    }
                } else if (!over?.id.startsWith('day-') && !over?.id.startsWith('unscheduled-zone')) {
                    if (timerRef.current) {
                        clearTimeout(timerRef.current);
                        timerRef.current = null;
                    }
                    if (progressRef.current) {
                        clearInterval(progressRef.current);
                        progressRef.current = null;
                    }
                    setDragOverWeek(null);
                    setSwitchProgress(0);
                }
            },
            onDragEnd: () => {
                if (timerRef.current) {
                    clearTimeout(timerRef.current);
                    timerRef.current = null;
                }
                if (progressRef.current) {
                    clearInterval(progressRef.current);
                    progressRef.current = null;
                }
                setDragOverWeek(null);
                setSwitchProgress(0);
            },
            onDragCancel: () => {
                if (timerRef.current) {
                    clearTimeout(timerRef.current);
                    timerRef.current = null;
                }
                if (progressRef.current) {
                    clearInterval(progressRef.current);
                    progressRef.current = null;
                }
                setDragOverWeek(null);
                setSwitchProgress(0);
            }
        });

        return null;
    };

    const DroppableWeekButton = ({ week, index, label }) => {
        const { setNodeRef } = useDroppable({
            id: `week-selector-${index}`,
            disabled: isPanelOpen, // Disable when driver panel is open
            data: {
                type: 'week-selector',
            }
        });

        return (
            <button
                ref={setNodeRef}
                onClick={() => setSelectedWeek(`week${week}`)}
                className={`relative px-6 py-3 rounded-lg font-medium transition-all duration-200 overflow-hidden ${selectedWeek === `week${week}`
                    ? "bg-white text-teal-600 shadow-sm"
                    : dragOverWeek === index && switchProgress > 0
                        ? "bg-teal-500 text-white shadow-md transform scale-105"
                        : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                    }`}
            >
                <div className="flex flex-col justify-center">
                    <span className="relative z-10 text-base">Week {week}</span>
                    <span className="relative z-10 text-teal-800/60 text-sm">{label}</span>
                </div>

                {dragOverWeek === index && switchProgress > 0 && (
                    <>
                        <div
                            className="absolute bottom-0 left-0 h-1 bg-white opacity-80 transition-all duration-100 ease-out z-20"
                            style={{ width: `${switchProgress}%` }}
                        />
                        <div className="absolute inset-0 flex items-center justify-center w-full h-full">
                            <span className="w-full h-full text-xs text-white font-semibold bg-teal-600 px-2 py-1 rounded z-10 flex items-center justify-center">
                                Switching...
                            </span>
                        </div>
                    </>
                )}
            </button>
        );
    };

    // Driver panel handlers
    const openPanel = () => {
        setIsPanelOpen(true);
    };

    const closePanel = () => {
        setIsPanelOpen(false);
        setCurrentDropTarget(null);
        setDropTargetRect(null);
    };

    const handleDriverDragStart = (event) => {
        setActiveId(event.active.id);
        setDragHandleType('driver');

        const assignment = event.active.data.current?.assignment;
        if (assignment) {
            setDraggedItemCache(assignment);
            openPanel();
        }
    };

    const handleDriverDragOver = (event) => {
        const { over } = event;

        if (over && over.id.startsWith('driver-')) {
            const driverId = over.id.replace('driver-', '');
            setCurrentDropTarget(driverId);

            // Get the target element's position
            const element = document.getElementById(over.id);
            if (element) {
                const rect = element.getBoundingClientRect();
                setDropTargetRect(rect);
            }
        } else {
            setCurrentDropTarget(null);
            setDropTargetRect(null);
        }
    };

    const handleDriverDragEnd = async (event) => {
        const { active, over } = event;

        console.log("driver drag ended", { event, collisions: event.collisions });

        setDragOverWeek(null);
        setSwitchProgress(0);
        const assignmentData = draggedItemCache || active.data.current?.assignment;
        setDraggedItemCache(null);

        console.log("over:", over); // returns null

        // If no drop target, just close the panel and exit
        if (!over) {
            console.log("No drop target detected - closing panel");
            setActiveId(null);
            setDragHandleType(null);
            closePanel();
            return;
        }

        console.log("Drop target ID:", over.id);

        if (dragHandleType === 'driver') {
            // Handle remove driver zone
            if (over.id === 'remove-driver-zone') {
                console.log("Removing driver from assignment");
                // Remove driver from all deliveries of this job
                const deliveries = assignmentData.job_deliveries || [];
                const removePromises = deliveries
                    .filter(d => d.assigned_driver_id) // Only remove from deliveries that have a driver
                    .map(delivery => removeDriver(delivery.id));

                await Promise.all(removePromises);
                closePanel();
            }
            // Handle driver assignment
            else if (over.id.startsWith('driver-')) {
                const driverId = over.id.replace('driver-', '');
                const driver = drivers.find(d => d.id === driverId);

                console.log(driver);

                if (driver && assignmentData) {
                    console.log("calling assign driver");
                    await assignDriver(assignmentData.id, driver.id, driver.full_name || driver.username);
                }

                closePanel();
            }
        } else if (dragHandleType === 'date') {
            // Handle date drag normally - existing logic
            if (!over) {
                setActiveId(null);
                setDragHandleType(null);
                return;
            }

            if (over.id.toString().startsWith('week-selector-')) {
                setActiveId(null);
                setDragHandleType(null);
                return;
            }

            if (over.id === 'unscheduled-zone') {
                setPendingMove({
                    assignment: assignmentData,
                    removeDate: true
                });
                setShowConfirmation(true);
            } else if (over.id.startsWith('day-')) {
                const [, weekStr, dayKey] = over.id.split('-');
                const dayIndex = days.findIndex(d => d.key === dayKey);

                if (dayIndex !== -1) {
                    const targetDate = getDayDateObject(dayIndex);
                    const newDate = new Date(targetDate);
                    newDate.setUTCHours(10, 0, 0, 0);

                    setPendingMove({
                        assignment: assignmentData,
                        newDate: newDate,
                        dayKey: dayKey,
                        targetWeek: weekStr
                    });

                    setShowConfirmation(true);
                }
            }
        }

        setActiveId(null);
        setDragHandleType(null);
        closePanel();
    };

    const assignDriver = async (jobId, driverId, driverName) => {
        console.log("called assign driver")
        try {
            // Find the assignment (job) to get its delivery records
            const assignment = unscheduledAssignments.find(a => a.id === jobId) ||
                Object.values(scheduleData).flatMap(week =>
                    Object.values(week).flat()
                ).find(a => a.id === jobId);

            if (!assignment) {
                console.error('Assignment not found:', jobId);
                alert('Assignment not found. Please refresh the page.');
                return;
            }

            // Get all delivery records for this job
            let deliveries = assignment.job_deliveries || [];

            // If no deliveries exist, create them based on job_quotes
            if (deliveries.length === 0) {
                console.log('No deliveries found, creating delivery records for job:', jobId);

                // Get the quote types that are required
                const quotes = assignment.job_quotes || [];
                const requiredQuotes = quotes.filter(q => q.is_required);

                if (requiredQuotes.length === 0) {
                    console.error('No required quotes found for job:', jobId);
                    alert('This job has no delivery items. Please add quotes first.');
                    return;
                }

                // Create delivery records for each required quote
                const createPromises = requiredQuotes.map(async (quote) => {
                    const { data, error } = await supabase
                        .from('job_deliveries')
                        .insert({
                            job_id: jobId,
                            quote_type: quote.quote_type,
                            is_required: true,
                            delivery_status: 'not_scheduled',
                            delivery_address: assignment.customer_address,
                            delivery_contact_name: assignment.customer_name,
                            delivery_contact_phone: assignment.customer_number
                        })
                        .select()
                        .single();

                    if (error) {
                        console.error('Error creating delivery record:', error);
                        throw error;
                    }
                    return data;
                });

                deliveries = await Promise.all(createPromises);
                console.log('Created delivery records:', deliveries);
            }

            // Assign driver to all deliveries of this job
            const assignmentPromises = deliveries.map(delivery =>
                assignDriverToDelivery(
                    delivery.id, // Use job_deliveries.id, not jobs.id
                    driverId,
                    driverName,
                    userProfile.id,
                    userProfile.full_name || userProfile.username
                )
            );

            await Promise.all(assignmentPromises);

            // Refresh assignments and driver workloads
            await fetchAssignments();
            await fetchDrivers();
        } catch (error) {
            console.error('Error assigning driver:', error);
            alert('Failed to assign driver. Please try again.');
        }
    };

    const removeDriver = async (deliveryId) => {
        try {
            await removeDriverFromDelivery(deliveryId);

            // Refresh assignments and driver workloads
            await fetchAssignments();
            await fetchDrivers();
        } catch (error) {
            console.error('Error removing driver:', error);
        }
    };

    // Mobile handlers
    const handleMobileMove = (assignment, e) => {
        e.stopPropagation();
        setSelectedAssignment(assignment);
        setShowMoveModal(true);
    };

    const handleMobileMoveConfirm = (targetWeek, targetDay) => {
        const dayIndex = days.findIndex(day => day.key === targetDay);
        const targetDate = getDayDateObject(dayIndex);

        const newDate = new Date(targetDate);
        newDate.setUTCHours(10, 0, 0, 0);

        setPendingMove({
            assignment: selectedAssignment,
            newDate: newDate,
            dayKey: targetDay,
            targetWeek: targetWeek
        });

        setShowMoveModal(false);
        setShowConfirmation(true);
    };

    const handleConfirmMove = async () => {
        if (!pendingMove) return;

        try {
            setIsLoading(true);

            // Get quote type from the assignment card
            const quoteType = pendingMove.assignment.quote_info?.quote_type;
            if (!quoteType) {
                throw new Error('Quote type not found on assignment');
            }

            const { error } = await updateDeliveryDate(
                pendingMove.assignment.id,
                quoteType,
                pendingMove.removeDate ? null : pendingMove.newDate
            );

            if (error) throw error;

            await fetchAssignments();
        } catch (error) {
            console.error("Failed to update delivery date:", error);
            alert("Failed to update delivery schedule. Please try again.");
        } finally {
            setIsLoading(false);
            setShowConfirmation(false);
            setPendingMove(null);
            setActiveId(null);
        }
    };

    const handleCancelMove = () => {
        setShowConfirmation(false);
        setPendingMove(null);
        setActiveId(null);
        setDragOverWeek(null);
        setSwitchProgress(0);
    };

    // Draggable Components
    const DraggableAssignmentCard = ({ assignment, isUnscheduled = false }) => {
        // Get quote type first - need it for unique IDs
        const quoteType = assignment.quote_info?.quote_type;

        // IMPORTANT: Create unique IDs by combining job ID + quote type
        // This prevents conflicts when multiple quotes from the same job are displayed
        const uniqueCardId = `${assignment.id}-${quoteType}`;

        // Date drag handle
        const dateHandle = useDraggable({
            id: `${uniqueCardId}-date`,
            data: { assignment, handleType: 'date' },
            disabled: userProfile?.role !== 'admin' && userProfile?.role !== 'super_admin',
        });

        // Driver drag handle
        const driverHandle = useDraggable({
            id: `${uniqueCardId}-driver`,
            data: { assignment, handleType: 'driver' },
            disabled: userProfile?.role !== 'admin' && userProfile?.role !== 'super_admin',
        });

        const isDragging = dateHandle.isDragging || driverHandle.isDragging;

        // Check if assignment has both date and driver
        const hasDate = !!assignment.quote_info?.expected_finish_date;

        // Check if delivery for this quote has an assigned driver
        const delivery = assignment.job_deliveries?.find(d => d.quote_type === quoteType);
        const hasDriver = !!delivery?.assigned_driver_id;
        const driverName = delivery?.assigned_driver_name || 'Set Driver';

        // Get quote type styling
        const quoteStyle = getQuoteTypeStyle(quoteType);
        const QuoteIcon = quoteStyle.icon;

        return (
            <div
                className={`bg-white border-2 border-gray-200 rounded-lg p-3 shadow-sm hover:shadow-md transition-all ${isDragging ? 'opacity-50' : ''
                    } ${userProfile?.role === 'admin' || userProfile?.role === 'super_admin' ? '' : 'cursor-pointer'}`}
                onClick={() => navigate(`/${companySlug}/assignment/${assignment.id}`)}
            >
                <div className={`w-full h-1 rounded-full mb-3 ${getMainStatusColor(assignment.main_status)}`}></div>

                <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                        <FiHash className="w-3 h-3 text-gray-400 flex-shrink-0" />
                        <span className="font-semibold text-gray-900 text-sm truncate">
                            {assignment.unique_id}
                        </span>
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${quoteStyle.bgColor} ${quoteStyle.textColor} flex-shrink-0`}>
                            <QuoteIcon className="w-3 h-3" />
                            {quoteStyle.label}
                        </span>
                    </div>
                    <div className="flex items-center gap-2">
                        {(assignment.urgent_quote || assignment.urgent_design) && (
                            <span className="bg-red-100 text-red-700 text-xs font-bold px-2 py-1 rounded-full">
                                URGENT
                            </span>
                        )}
                        {isMobile && (userProfile?.role === 'admin' || userProfile?.role === 'super_admin') && (
                            <button
                                onClick={(e) => handleMobileMove(assignment, e)}
                                className="p-1 text-gray-400 hover:text-teal-600 transition-colors"
                            >
                                <FiMove className="w-4 h-4" />
                            </button>
                        )}
                    </div>
                </div>

                <div className="flex items-center gap-2 mb-3">
                    <FiUser className="w-3 h-3 text-teal-500 flex-shrink-0" />
                    <span className="text-sm text-gray-700 truncate font-medium">
                        {assignment.customer_name}
                    </span>
                </div>

                <div className="mb-3">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-teal-100 text-teal-800">
                        <FiTruck className="w-3 h-3 mr-1" />
                        {assignment.job_type?.replace('_', ' & ')}
                    </span>
                </div>

                {/* Dual handle system for all items (admin only) */}
                {(userProfile?.role === 'admin' || userProfile?.role === 'super_admin') ? (
                    <div className="flex items-center gap-2 pt-2 border-t border-gray-100">
                        {/* Date Handle */}
                        <div
                            ref={dateHandle.setNodeRef}
                            {...dateHandle.attributes}
                            {...dateHandle.listeners}
                            className={`flex-1 flex items-center justify-center gap-1 px-3 py-2 rounded ${hasDate ? 'bg-teal-100 text-teal-700' : 'bg-gray-100 text-gray-600'
                                } cursor-grab active:cursor-grabbing hover:shadow-md transition-all`}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <FiCalendar className="w-3 h-3" />
                            <span className="text-xs font-medium">
                                {hasDate
                                    ? new Date(assignment.quote_info.expected_finish_date).toLocaleDateString('en-AU', { month: 'short', day: 'numeric' })
                                    : 'Set Date'
                                }
                            </span>
                        </div>

                        {/* Driver Handle */}
                        <div
                            ref={driverHandle.setNodeRef}
                            {...driverHandle.attributes}
                            {...driverHandle.listeners}
                            className={`flex-1 flex items-center justify-center gap-1 px-3 py-2 rounded ${hasDriver ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
                                } cursor-grab active:cursor-grabbing hover:shadow-md transition-all`}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <FiUsers className="w-3 h-3" />
                            <span className="text-xs font-medium truncate">
                                {driverName}
                            </span>
                        </div>
                    </div>
                ) : (
                    <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                        <div className="flex items-center gap-1">
                            <FiCalendar className="w-3 h-3 text-gray-400" />
                            <span className="text-xs text-gray-500">
                                {assignment.booking_delivery_date
                                    ? new Date(assignment.booking_delivery_date).toLocaleDateString('en-AU')
                                    : 'No date'
                                }
                            </span>
                        </div>
                        {hasDriver && (
                            <div className="flex items-center gap-1">
                                <FiUsers className="w-3 h-3 text-gray-400" />
                                <span className="text-xs text-gray-500 truncate max-w-[120px]">
                                    {driverName}
                                </span>
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    };

    const DroppableDay = ({ weekKey, dayKey, dayIndex, children }) => {
        const { isOver, setNodeRef } = useDroppable({
            id: `day-${weekKey}-${dayKey}`,
            disabled: isPanelOpen, // Disable when driver panel is open
        });

        return (
            <div
                ref={setNodeRef}
                className={`bg-gray-50 border-2 ${isOver ? 'border-teal-300 bg-teal-50' : 'border-gray-200'
                    } rounded-xl overflow-hidden hover:border-teal-300 transition-colors min-h-[300px]`}
            >
                <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-3 py-2 border-b border-gray-200">
                    <div className="text-center">
                        <h3 className="font-bold text-gray-900 text-sm">{days[dayIndex].label}</h3>
                        <p className="text-xs text-gray-500">{getDayDate(dayIndex)}</p>
                        {dayKey.toLowerCase() === todayLabel.toLowerCase() && weekKey === "week1" && (
                            <span className="bg-green-100 text-green-800 text-xs font-medium px-2 py-1 rounded-full mt-1 inline-block">
                                Today
                            </span>
                        )}
                    </div>
                </div>
                <div className="p-2 space-y-2">
                    {children}
                </div>
            </div>
        );
    };

    const DroppableUnscheduled = ({ children }) => {
        const { isOver, setNodeRef } = useDroppable({
            id: 'unscheduled-zone',
            disabled: isPanelOpen, // Disable when driver panel is open
        });

        return (
            <div
                ref={setNodeRef}
                className={`bg-white border-2 border-dashed ${isOver ? 'border-orange-300 bg-orange-50' : 'border-gray-300'
                    } rounded-xl p-6 hover:border-orange-300 transition-colors`}
            >
                {children}
            </div>
        );
    };

    // Droppable zone for driver panel
    const DroppableDriverZone = ({ id, className, children, disabled }) => {
        const { isOver, setNodeRef } = useDroppable({
            id: id,
            disabled: disabled,
        });

        return (
            <div
                id={id}
                ref={setNodeRef}
                className={`${className} ${isOver && !disabled ? 'ring-2 ring-blue-500 ring-offset-2' : ''}`}
            >
                {children}
            </div>
        );
    };

    // Mobile List View
    const MobileListView = () => (
        <div className="space-y-3">
            {days.map(({ label, key }, dayIndex) => {
                const dayAssignments = scheduleData[selectedWeek]?.[key] || [];
                return (
                    <div key={key} className="bg-white rounded-lg border border-gray-200 shadow-sm">
                        <div
                            className="px-4 py-3 bg-gray-50 rounded-t-lg cursor-pointer hover:bg-gray-100 transition-colors"
                            onClick={() => setSelectedDay(selectedDay === key ? null : key)}
                        >
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                                        <span className="font-bold text-teal-600 text-sm">{label}</span>
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-gray-900">{`${label}, ${getDayDate(dayIndex)}`}</h3>
                                        <p className="text-sm text-gray-500">{dayAssignments.length} delivery{dayAssignments.length !== 1 ? 's' : ''}</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    {label.toLowerCase() === todayLabel.toLowerCase() && selectedWeek === "week1" && (
                                        <span className="bg-green-100 text-green-800 text-xs font-medium px-2 py-1 rounded-full">
                                            Today
                                        </span>
                                    )}
                                    <FiChevronRight className={`w-4 h-4 text-gray-400 transition-transform ${selectedDay === key ? 'rotate-90' : ''
                                        }`} />
                                </div>
                            </div>
                        </div>

                        {selectedDay === key && (
                            <div className="p-4 border-t border-gray-100 space-y-3">
                                {dayAssignments.length > 0 ? dayAssignments.map((assignment) => (
                                    <DraggableAssignmentCard key={`${assignment.id}-${assignment.quote_info?.quote_type}`} assignment={assignment} />
                                )) : (
                                    <div className="text-center py-6">
                                        <FiTruck className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                                        <p className="text-gray-500">No deliveries scheduled</p>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );

    const draggedAssignment = draggedItemCache || (activeId
        ? [...Object.values(scheduleData[selectedWeek] || {}).flat(), ...unscheduledAssignments]
            .find(a => a.id === activeId)
        : null);

    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
                <span className="ml-3 text-gray-600">Loading delivery schedule...</span>
            </div>
        );
    }

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={customCollisionDetection}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragEnd={handleDragEnd}
            measuring={{
                droppable: {
                    strategy: MeasuringStrategy.Always,
                },
            }}
        >
            <>
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-2">
                            <FiTruck className="w-8 h-8 text-teal-600" />
                            Delivery Schedule
                        </h1>
                        <p className="text-gray-600 mt-1">Manage and track delivery timelines</p>
                    </div>
                    <div className="flex items-center gap-3">
                        {isMobile && (
                            <div className="flex bg-gray-100 rounded-lg p-1">
                                <button
                                    onClick={() => setMobileViewMode('grid')}
                                    className={`p-2 rounded text-sm font-medium transition-colors ${mobileViewMode === 'grid' ? 'bg-white text-teal-600 shadow-sm' : 'text-gray-600'
                                        }`}
                                >
                                    <FiGrid className="w-4 h-4" />
                                </button>
                                <button
                                    onClick={() => setMobileViewMode('list')}
                                    className={`p-2 rounded text-sm font-medium transition-colors ${mobileViewMode === 'list' ? 'bg-white text-teal-600 shadow-sm' : 'text-gray-600'
                                        }`}
                                >
                                    <FiList className="w-4 h-4" />
                                </button>
                            </div>
                        )}
                        <div className="text-sm text-gray-500 bg-gray-50 px-3 py-2 rounded-lg">
                            Australian Eastern Time
                        </div>
                    </div>
                </div>

                {/* Filters */}
                <div className="space-y-4 border-b border-gray-200 pb-6">
                    <div className="flex flex-col sm:flex-row gap-4">
                        {userProfile?.role === 'driver' && (
                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    id="showOnlyMine"
                                    checked={showOnlyMine}
                                    onChange={(e) => setShowOnlyMine(e.target.checked)}
                                    className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
                                />
                                <label htmlFor="showOnlyMine" className="ml-2 text-sm text-gray-700">
                                    Show only my deliveries
                                </label>
                            </div>
                        )}

                        {(userProfile?.role === 'admin' || userProfile?.role === 'super_admin') && (
                            <div className="flex items-center gap-2">
                                <label className="text-sm text-gray-700">Filter by driver:</label>
                                <select
                                    value={selectedUser}
                                    onChange={(e) => setSelectedUser(e.target.value)}
                                    className="border border-gray-300 rounded-md px-3 py-1 text-sm focus:ring-teal-500 focus:border-teal-500 min-w-[200px]"
                                >
                                    <option value="">All drivers</option>
                                    {availableDrivers.map(driver => (
                                        <option key={driver.id} value={driver.id}>
                                            {driver.username || driver.full_name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}
                    </div>
                </div>

                {/* Week Navigation */}
                <div className="flex items-center justify-between">
                    <button
                        onClick={() => {
                            const currentWeek = parseInt(selectedWeek.replace('week', ''));
                            if (currentWeek > 1) setSelectedWeek(`week${currentWeek - 1}`);
                        }}
                        disabled={selectedWeek === 'week1'}
                        className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <FiChevronLeft className="w-5 h-5" />
                    </button>

                    <div className="text-center">
                        <h2 className="font-semibold text-gray-900">
                            Week {selectedWeek.replace('week', '')}
                        </h2>
                        <p className="text-sm text-gray-500">
                            {getWeekLabels()[parseInt(selectedWeek.replace('week', '')) - 1]}
                        </p>
                    </div>
                    <button
                        onClick={() => {
                            const currentWeek = parseInt(selectedWeek.replace('week', ''));
                            if (currentWeek < 4) setSelectedWeek(`week${currentWeek + 1}`);
                        }}
                        disabled={selectedWeek === 'week4'}
                        className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <FiChevronRight className="w-5 h-5" />
                    </button>
                </div>

                {/* Week Selection Pills */}
                <div className="flex justify-center">
                    <WeekSwitcher />
                    {isMobile ? (
                        <div className="flex bg-gray-100 rounded-lg p-1">
                            {[1, 2, 3, 4].map((week) => (
                                <button
                                    key={week}
                                    onClick={() => setSelectedWeek(`week${week}`)}
                                    className={`px-4 py-2 rounded text-sm font-medium transition-colors ${selectedWeek === `week${week}`
                                        ? 'bg-white text-teal-600 shadow-sm'
                                        : 'text-gray-600 hover:text-gray-900'
                                        }`}
                                >
                                    {week}
                                </button>
                            ))}
                        </div>
                    ) : (
                        <div className="flex bg-gray-100 rounded-xl p-1 relative gap-2">
                            {[1, 2, 3, 4].map((week, index) => (
                                <DroppableWeekButton
                                    key={week}
                                    week={week}
                                    index={index}
                                    label={getWeekLabels()[index]}
                                />
                            ))}
                        </div>
                    )}
                </div>

                {/* Content - Grid or List View */}
                {isMobile && mobileViewMode === 'list' ? (
                    <MobileListView />
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
                        {days.map(({ label, key }, dayIndex) => {
                            const dayAssignments = scheduleData[selectedWeek]?.[key] || [];
                            return (
                                <DroppableDay
                                    key={key}
                                    weekKey={selectedWeek}
                                    dayKey={key}
                                    dayIndex={dayIndex}
                                >
                                    {dayAssignments.length > 0 ? (
                                        dayAssignments.map((assignment) => (
                                            <DraggableAssignmentCard key={`${assignment.id}-${assignment.quote_info?.quote_type}`} assignment={assignment} />
                                        ))
                                    ) : (
                                        <div className="text-center py-8">
                                            <FiTruck className="w-6 h-6 text-gray-300 mx-auto mb-2" />
                                            <p className="text-xs text-gray-500">No deliveries</p>
                                        </div>
                                    )}
                                </DroppableDay>
                            );
                        })}
                    </div>
                )}

                {/* Unscheduled Deliveries Section */}
                <DroppableUnscheduled>
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-gray-900">Unscheduled Deliveries</h3>
                        <div className="flex items-center gap-2">
                            <span className="bg-orange-100 text-orange-800 text-sm font-medium px-3 py-1 rounded-full">
                                {unscheduledAssignments?.length || 0} deliveries
                            </span>
                            {!isMobile && (userProfile?.role === 'admin' || userProfile?.role === 'super_admin') && (
                                <span className="text-xs text-gray-500">Drop here to unschedule</span>
                            )}
                        </div>
                    </div>

                    {(!unscheduledAssignments || unscheduledAssignments.length === 0) ? (
                        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                            <FiPackage className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                            <p className="text-gray-500 font-medium">All deliveries have been scheduled</p>
                            <p className="text-sm text-gray-400 mt-1">Great job!</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                            {unscheduledAssignments.map((assignment) => (
                                <DraggableAssignmentCard key={`${assignment.id}-${assignment.quote_info?.quote_type}`} assignment={assignment} isUnscheduled={true} />
                            ))}
                        </div>
                    )}
                </DroppableUnscheduled>
            </>

            {/* Mobile Move Modal */}
            {showMoveModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-t-xl sm:rounded-xl w-full max-w-md max-h-[80vh] overflow-hidden">
                        <div className="flex items-center justify-between p-4 border-b border-gray-200">
                            <h3 className="text-lg font-semibold text-gray-900">Move Delivery</h3>
                            <button
                                onClick={() => setShowMoveModal(false)}
                                className="p-2 text-gray-400 hover:text-gray-600"
                            >
                                <FiX className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="p-4">
                            <div className="mb-4">
                                <div className="bg-gray-50 p-3 rounded-lg">
                                    <div className="flex items-center gap-2 mb-1">
                                        <FiHash className="w-4 h-4 text-gray-400" />
                                        <span className="font-medium">{selectedAssignment?.unique_id}</span>
                                        {selectedAssignment?.quote_info?.quote_type && (() => {
                                            const modalQuoteStyle = getQuoteTypeStyle(selectedAssignment.quote_info.quote_type);
                                            const ModalQuoteIcon = modalQuoteStyle.icon;
                                            return (
                                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${modalQuoteStyle.bgColor} ${modalQuoteStyle.textColor}`}>
                                                    <ModalQuoteIcon className="w-3 h-3" />
                                                    {modalQuoteStyle.label}
                                                </span>
                                            );
                                        })()}
                                    </div>
                                    <div className="flex items-center gap-2 text-sm text-gray-600">
                                        <FiUser className="w-3 h-3" />
                                        <span>{selectedAssignment?.customer_name}</span>
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-4 max-h-60 overflow-y-auto">
                                {[1, 2, 3, 4].map((weekNum) => (
                                    <div key={weekNum}>
                                        <h4 className="font-medium text-gray-900 mb-2">
                                            Week {weekNum} - {getWeekLabels()[weekNum - 1]}
                                        </h4>
                                        <div className="grid grid-cols-3 gap-2">
                                            {days.map(({ label, key }) => (
                                                <button
                                                    key={`${weekNum}-${key}`}
                                                    onClick={() => handleMobileMoveConfirm(`week${weekNum}`, key)}
                                                    className="p-3 text-center border border-gray-200 rounded-lg hover:border-teal-300 hover:bg-teal-50 transition-colors"
                                                >
                                                    <div className="font-medium text-sm">{label}</div>
                                                    <div className="text-xs text-gray-500 mt-1">
                                                        {(() => {
                                                            const today = getAustralianDate();
                                                            const monday = new Date(today);
                                                            monday.setDate(today.getDate() - today.getDay() + 1);
                                                            const targetDate = new Date(monday);
                                                            targetDate.setDate(monday.getDate() + ((weekNum - 1) * 7) + days.findIndex(d => d.key === key));
                                                            return targetDate.toLocaleDateString('en-AU', { day: '2-digit', month: 'short' });
                                                        })()}
                                                    </div>
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Confirmation Popup */}
            <ConfirmationPopup
                isOpen={showConfirmation}
                title="Confirm Delivery Schedule Change"
                message={pendingMove
                    ? `Are you sure you want to reschedule delivery for "${pendingMove.assignment.unique_id}" to ${pendingMove?.newDate ? pendingMove.newDate.toLocaleDateString('en-AU') : "unscheduled"
                    }?`
                    : ""
                }
                confirmText="Reschedule Delivery"
                cancelText="Cancel"
                onConfirm={handleConfirmMove}
                onCancel={handleCancelMove}
            />

            {/* Driver Assignment Panel */}
            <div className={`fixed inset-0 z-50 ${isPanelOpen ? '' : 'pointer-events-none'}`}>
                {/* Backdrop */}
                {isPanelOpen && (
                    <div
                        onClick={closePanel}
                        className="fixed inset-0 bg-black/40 transition-opacity duration-300 backdrop-blur-sm"
                    />
                )}

                <div
                    ref={panelRef}
                    className="fixed left-0 right-0 h-[85vh] max-h-[85vh] transition-all duration-500 ease-out pointer-events-auto"
                    style={{
                        bottom: isPanelOpen ? 0 : '-85vh'
                    }}
                >
                    <div className="bg-white rounded-t-3xl shadow-2xl h-full flex flex-col relative overflow-hidden pointer-events-auto">
                        {/* Header */}
                        <div className={`relative px-6 py-4 transition-colors duration-300 ${currentDropTarget?.startsWith('driver-')
                            ? 'bg-gradient-to-br from-green-900 via-emerald-800 to-green-900'
                            : 'bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900'
                            }`}>
                            {/* Background Pattern */}
                            <div className="absolute inset-0 opacity-10">
                                <div className="absolute inset-0" style={{
                                    backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
                                    backgroundSize: '32px 32px'
                                }}></div>
                            </div>

                            <div className="max-w-7xl mx-auto relative z-10">
                                <div className="flex items-center justify-between gap-6">
                                    {/* Title */}
                                    <div className="flex-1">
                                        <h3 className="text-2xl font-bold text-white mb-1">
                                            Assign Driver
                                        </h3>
                                        <p className="text-white/70 text-sm">
                                            Select a driver for this delivery
                                        </p>
                                    </div>

                                    {/* Assignment Info */}
                                    {draggedItemCache && (() => {
                                        const cacheQuoteStyle = getQuoteTypeStyle(draggedItemCache.quote_info?.quote_type);
                                        const CacheQuoteIcon = cacheQuoteStyle.icon;
                                        return (
                                            <div className="flex items-center gap-3">
                                                <div className="bg-white rounded-xl shadow-lg px-4 py-2.5 border border-gray-100 min-w-[250px]">
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <FiHash className="w-4 h-4 text-gray-400" />
                                                        <span className="font-bold text-sm text-gray-900">
                                                            {draggedItemCache.unique_id}
                                                        </span>
                                                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cacheQuoteStyle.bgColor} ${cacheQuoteStyle.textColor}`}>
                                                            <CacheQuoteIcon className="w-3 h-3" />
                                                            {cacheQuoteStyle.label}
                                                        </span>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <FiUser className="w-3 h-3 text-gray-400" />
                                                        <span className="text-xs text-gray-600 truncate">
                                                            {draggedItemCache.customer_name}
                                                        </span>
                                                    </div>
                                                </div>

                                                {/* Close Button */}
                                                <button
                                                    onClick={closePanel}
                                                    className="p-2 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-lg transition-all border border-white/10"
                                                >
                                                    <FiX className="w-5 h-5 text-white" />
                                                </button>
                                            </div>
                                        );
                                    })()}

                                    {/* Close Button when no assignment */}
                                    {!draggedItemCache && (
                                        <button
                                            onClick={closePanel}
                                            className="p-2 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-lg transition-all border border-white/10"
                                        >
                                            <FiX className="w-5 h-5 text-white" />
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Remove Driver Zone */}
                        {draggedItemCache && draggedItemCache.job_deliveries?.some(d => d.assigned_driver_id) && (
                            <div className="px-8 pt-6 pb-4 border-b border-gray-200 bg-gray-50">
                                <div className="max-w-7xl mx-auto">
                                    <DroppableDriverZone
                                        id="remove-driver-zone"
                                        className="border-2 border-dashed border-red-300 bg-white rounded-lg p-4 text-center hover:bg-red-50 hover:border-red-400 transition-all group cursor-pointer"
                                        disabled={false}
                                    >
                                        <div className="flex items-center justify-center gap-3">
                                            <div className="p-2 bg-red-100 rounded-lg group-hover:bg-red-200 transition-colors">
                                                <FiX className="w-5 h-5 text-red-600" />
                                            </div>
                                            <div className="text-left">
                                                <p className="text-sm font-semibold text-red-900">Remove Driver</p>
                                                <p className="text-xs text-red-600">Unassign driver from this delivery</p>
                                            </div>
                                        </div>
                                    </DroppableDriverZone>
                                </div>
                            </div>
                        )}

                        {/* Driver List */}
                        <div className="flex-1 overflow-y-auto p-8">
                            <div className="max-w-7xl mx-auto">
                                <div className="flex items-center justify-between mb-6">
                                    <h4 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                                        <FiUsers className="w-5 h-5 text-gray-600" />
                                        Available Drivers
                                    </h4>
                                    <span className="px-4 py-2 bg-gray-100 text-gray-700 rounded-full text-sm font-medium">
                                        {drivers.length} {drivers.length === 1 ? 'driver' : 'drivers'}
                                    </span>
                                </div>

                                {drivers.length === 0 ? (
                                    <div className="text-center py-20 bg-gray-50 rounded-2xl border-2 border-dashed border-gray-300">
                                        <div className="p-6 bg-gray-100 rounded-full w-24 h-24 mx-auto mb-4 flex items-center justify-center">
                                            <FiTruck className="w-12 h-12 text-gray-400" />
                                        </div>
                                        <p className="text-lg font-semibold text-gray-700 mb-2">No drivers available</p>
                                        <p className="text-sm text-gray-500">Add drivers to start assigning deliveries</p>
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                                        {drivers.map((driver) => {
                                            const workload = driverWorkloads[driver.id] || 0;
                                            // Check if any delivery in the job is already assigned to this driver
                                            const alreadyAssigned = draggedItemCache?.job_deliveries?.some(d => d.assigned_driver_id === driver.id) || false;

                                            return (
                                                <DroppableDriverZone
                                                    key={driver.id}
                                                    id={`driver-${driver.id}`}
                                                    className={`border-2 rounded-2xl p-5 transition-all cursor-pointer ${alreadyAssigned
                                                        ? 'border-gray-300 bg-gray-50 opacity-50 cursor-not-allowed'
                                                        : 'border-gray-200 hover:border-blue-400 hover:bg-blue-50 hover:shadow-lg'
                                                        }`}
                                                    disabled={alreadyAssigned}
                                                >
                                                    <div className="pointer-events-none">
                                                        <div className="flex items-start justify-between mb-4">
                                                            <div className="flex items-center gap-3 flex-1 min-w-0">
                                                                <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-blue-200 rounded-xl flex items-center justify-center flex-shrink-0">
                                                                    <FiTruck className="w-6 h-6 text-blue-600" />
                                                                </div>
                                                                <div className="flex-1 min-w-0">
                                                                    <p className="font-semibold text-base text-gray-900 truncate">
                                                                        {driver.full_name || driver.username}
                                                                    </p>
                                                                    <p className="text-sm text-gray-500 truncate">
                                                                        {driver.phone || 'No phone'}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        <div className="flex items-center gap-2">
                                                            <span className={`text-xs font-bold px-3 py-1.5 rounded-full flex-shrink-0 ${workload === 0 ? 'bg-green-100 text-green-700' :
                                                                workload < 5 ? 'bg-yellow-100 text-yellow-700' :
                                                                    'bg-red-100 text-red-700'
                                                                }`}>
                                                                {workload} {workload === 1 ? 'Job' : 'Jobs'}
                                                            </span>
                                                            <span className={`text-xs font-bold flex-shrink-0 ${workload === 0 ? 'text-green-700' :
                                                                workload < 5 ? 'text-yellow-700' :
                                                                    'text-red-700'
                                                                }`}>
                                                                {workload === 0 ? "Available" : workload < 5 ? "Medium" : "High"} workload
                                                            </span>
                                                        </div>

                                                        {alreadyAssigned && (
                                                            <div className="mt-3 text-xs text-gray-500 text-center font-medium">
                                                                Already assigned
                                                            </div>
                                                        )}
                                                    </div>
                                                </DroppableDriverZone>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Drag Overlay */}
            <DragOverlay modifiers={[snapCenterToCursor]} dropAnimation={dropAnimationConfig}>
                {draggedAssignment ? (() => {
                    const overlayQuoteStyle = getQuoteTypeStyle(draggedAssignment.quote_info?.quote_type);
                    const OverlayQuoteIcon = overlayQuoteStyle.icon;
                    return (
                        <div
                            className="bg-white border-2 border-teal-500 rounded-lg p-3 shadow-2xl pointer-events-none"
                            style={{ opacity: 0.9 }}
                        >
                            <div className={`w-full h-1 rounded-full mb-3 ${getMainStatusColor(draggedAssignment.main_status)}`}></div>

                            <div className="flex items-start justify-between mb-3">
                                <div className="flex items-center gap-2 min-w-0 flex-1">
                                    <FiHash className="w-3 h-3 text-gray-400 flex-shrink-0" />
                                    <span className="font-semibold text-gray-900 text-sm truncate">
                                        {draggedAssignment.unique_id}
                                    </span>
                                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${overlayQuoteStyle.bgColor} ${overlayQuoteStyle.textColor} flex-shrink-0`}>
                                        <OverlayQuoteIcon className="w-3 h-3" />
                                        {overlayQuoteStyle.label}
                                    </span>
                                </div>
                                {(draggedAssignment.urgent_quote || draggedAssignment.urgent_design) && (
                                    <span className="bg-red-100 text-red-700 text-xs font-bold px-2 py-1 rounded-full">
                                        URGENT
                                    </span>
                                )}
                            </div>

                            <div className="flex items-center gap-2 mb-3">
                                <FiUser className="w-3 h-3 text-teal-500 flex-shrink-0" />
                                <span className="text-sm text-gray-700 truncate font-medium">
                                    {draggedAssignment.customer_name}
                                </span>
                            </div>
                        </div>
                    );
                })() : null}
            </DragOverlay>
        </DndContext>
    );
}

export default DeliverySchedule;